import type { KnowledgeHubEntry } from "@oracus/shared/api/knowledge";

import { useKnowledgeHub } from "../api/knowledge.api.ts";
import { MarkdownAnswer } from "../components/MarkdownAnswer.tsx";
import { Badge, Card, Page } from "../components/Page.tsx";

export function KnowledgeHub() {
  const hub = useKnowledgeHub();

  return (
    <Page
      title="Knowledge hub"
      description="Living documentation synthesized from your code and Jira, with citations back to the source. Updated automatically and reviewed before publishing."
    >
      {hub.isLoading && (
        <p className="text-sm text-zinc-500">Loading…</p>
      )}
      {hub.data && hub.data.entries.length === 0 && (
        <Card>
          <p className="text-sm text-zinc-600">
            No published entries yet. Curate topics under the{" "}
            <span className="font-medium">Manage</span> tab, generate a draft,
            and approve it to publish here.
          </p>
        </Card>
      )}
      <div className="space-y-6">
        {hub.data?.entries.map((entry) => (
          <HubEntry key={entry.topicId} entry={entry} />
        ))}
      </div>
    </Page>
  );
}

function HubEntry({ entry }: { entry: KnowledgeHubEntry }) {
  return (
    <Card>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-zinc-900">{entry.title}</h2>
        {entry.stale ? (
          <Badge tone="warn">Update pending</Badge>
        ) : (
          <Badge tone="success">Up to date</Badge>
        )}
      </div>
      <MarkdownAnswer content={entry.body} className="max-w-3xl" />
      {entry.citations.length > 0 && (
        <div className="mt-4 border-t border-zinc-100 pt-3">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
            Sources
          </p>
          <div className="flex flex-wrap gap-1.5">
            {entry.citations.map((c) => (
              <Badge key={`${c.sourceType}:${c.sourceId}`} tone="info">
                {c.label}
              </Badge>
            ))}
          </div>
        </div>
      )}
      {entry.publishedAt && (
        <p className="mt-3 text-xs text-zinc-400">
          Published {new Date(entry.publishedAt).toLocaleString()}
        </p>
      )}
    </Card>
  );
}
